import { Head, Link } from "@inertiajs/react";
import { Plus } from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDataTable } from "@/hooks/use-data-table";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import AppLayout from "@/layouts/app-layout";
import { companiesPath, newCompanyPath } from "@/routes";
import type { BreadcrumbItem, Company, PagyMetadata, RansackFilters } from "@/types";

import { CompaniesDataTable } from "./data-table";

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Compañías", href: companiesPath() },
];

interface CompaniesIndexProps {
  data: {
    companies: Company[];
    pagy: PagyMetadata;
  };
  filters: RansackFilters;
}

export default function CompaniesIndex({ data, filters }: CompaniesIndexProps) {
  const { companies, pagy } = data;
  const { handleFilterChange } = useDataTable({
    filters,
    pagy,
    path: companiesPath(),
  });

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useSyncedDebouncedValue(filters.name_or_legal_name_or_tax_id_cont ?? "");

  useEffect(() => {
    if ((filters.name_or_legal_name_or_tax_id_cont ?? "") !== debouncedSearchValue) {
      handleFilterChange({
        name_or_legal_name_or_tax_id_cont: debouncedSearchValue || undefined,
      });
    }
  }, [debouncedSearchValue]);

  const clearSearch = () => {
    setSearchValue("");
    handleFilterChange({ name_or_legal_name_or_tax_id_cont: undefined });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Compañías" />
      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Compañías</h2>
          <p className="text-muted-foreground">
            Gestiona las compañías con su información fiscal y logo
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-muted-foreground">
            Total: {pagy.count} compañías
          </div>

          <div className="flex w-full max-w-md items-center gap-2">
            <Input
              placeholder="Buscar por nombre, razón social o CUIT"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
            {searchValue ? (
              <Button
                type="button"
                variant="ghost"
                className="whitespace-nowrap"
                onClick={clearSearch}
              >
                Limpiar
              </Button>
            ) : null}
          </div>

          <div className="flex justify-end gap-x-2">
            <Button asChild>
              <Link href={newCompanyPath()}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Compañía
              </Link>
            </Button>
          </div>
        </div>

        <CompaniesDataTable companies={companies} pagy={pagy} filters={filters} />
      </div>
    </AppLayout>
  );
}
